import type { GameEvent, GameEventEnvelope, PhoneMessage, PhoneState } from './protocol';
import { newRoomCode, openChannel, type Channel } from './transport';

const TIMEOUT_MS = 5000;

/**
 * Game-side end of the phone link. Talks through the hosted relay when the
 * build has one (`VITE_RELAY_URL`), otherwise through Vite's dev WebSocket;
 * with neither it quietly does nothing.
 */
export class PhoneLink {
  /** Identifies this game tab; it doubles as the relay room code. */
  readonly session = newRoomCode();
  private readonly channel: Channel | null = openChannel('game', this.session);
  readonly available = !!this.channel;
  readonly kind = this.channel?.kind ?? null;
  remoteHold = false;
  private lastSeen = 0;
  private lastSent = 0;
  private wasConnected = false;
  onConnectionChange: ((connected: boolean) => void) | null = null;
  onReply: ((id: number, choice: number) => void) | null = null;
  onPutAway: (() => void) | null = null;
  onHello: (() => void) | null = null;
  onOpenApp: ((app: 'map' | 'chat') => void) | null = null;
  onTorch: (() => void) | null = null;

  constructor() {
    this.channel?.on('walkhome:phone', (msg: PhoneMessage) => {
      // Messages aimed at another game tab are not ours.
      if (msg.session && msg.session !== this.session) return;
      const first = !this.connected;
      this.lastSeen = performance.now();
      if (msg.kind === 'hold') this.remoteHold = msg.down;
      if (msg.kind === 'reply') this.onReply?.(msg.id, msg.choice);
      if (msg.kind === 'putAway') this.onPutAway?.();
      if (msg.kind === 'hello') this.onHello?.();
      if (msg.kind === 'openApp') this.onOpenApp?.(msg.app);
      if (msg.kind === 'torch') this.onTorch?.();
      if (first) this.checkConnection();
    });
  }

  get connected(): boolean {
    return this.available && performance.now() - this.lastSeen < TIMEOUT_MS;
  }

  /** Call every frame. Sends state at the channel's rate and tracks connection changes. */
  update(state: Omit<PhoneState, 'session'>): void {
    this.checkConnection();
    if (!this.channel) return;
    const now = performance.now();
    // Over the relay, only stream while a phone is actually listening.
    const interval = 1000 / this.channel.stateHz;
    if (now - this.lastSent < (this.channel.kind === 'relay' && !this.connected ? 2000 : interval)) return;
    this.lastSent = now;
    this.channel.send('walkhome:state', { ...state, session: this.session } satisfies PhoneState);
  }

  send(event: GameEvent): void {
    this.channel?.send('walkhome:event', { session: this.session, event } satisfies GameEventEnvelope);
  }

  private checkConnection(): void {
    const c = this.connected;
    if (!c) this.remoteHold = false;
    if (c !== this.wasConnected) {
      this.wasConnected = c;
      this.onConnectionChange?.(c);
    }
  }
}
